<template>
  <div :class="[module + '-admin']">
    <div class="admin-header">
      <h2>Administração de Insights</h2>
      <div class="button-container">
        <Button
          label="Configurar Insight"
          icon="pi pi-cog"
          class="p-button-primary mr-2"
          style="margin-right: 15px;"
          @click="openConfigModal"
        />
        <Button
          label="Insights"
          icon="pi pi-list"
          class="p-button-secondary"
          style="margin-right: 15px;"
          @click="openInsightsModal"
        />
        <Button
          label="Personas"
          icon="pi pi-user"
          severity="contrast"
          @click="openPersonasModal"
        />
      </div>
    </div>

    <InsightsConfigModal
      :showConfigModal="showConfigModal"
      :states="states"
      :roles="roles"
      :module="module"
      :moduleId="roleIdProp"
      :dataSourceOptions="$parent.dataSourceOptions"
      :socialMediaDataSourceOptions="socialMediaDataSourceOptions"
      @save="handleConfigSave"
      @update:showConfigModal="handleModalVisibility"
    />

    <Dialog
      :visible.sync="showInsightsModal"
      modal
      :style="{width: '80vw'}"
      :breakpoints="{'960px': '75vw', '640px': '90vw'}"
      header="Insights"
    >
      <div v-if="prompts && prompts.length > 0" class="prompt-cards-grid">
        <div 
          v-for="item in prompts"
          :key="item.id"
          class="prompt-card"
          :class="{ 'processing': isProcessing(item), 'editing': editingId === item.id }"
        >
          <div class="prompt-card-header">
            <div class="title-container">
              <input
                v-if="editingId === item.id"
                v-model="editingPrompt.title"
                type="text"
                class="edit-input"
                placeholder="Título do insight"
              />
              <h3 v-else>{{ item.title }}</h3>
            </div>
            <div v-if="item.status" :class="['status-badge', getStatusClass(item.status)]">
              {{ getStatusLabel(item.status) }}
            </div>
          </div>
          
          <div class="prompt-card-content">
            <!-- Modo de edição -->
            <div class="prompt-info" v-if="editingId === item.id">
              <StateCitySelector
                v-if="states && states.length"
                :states="states"
                :defaultState="editingPrompt.state"
                :defaultCity="editingPrompt.city"
                @update:city="updateCity"
                @update:state="updateState"
              />
            </div>
            <div class="prompt-info" v-else>
              <i class="pi pi-map-marker mr-2"></i>
              <span>{{ item.state }} {{ item.city ? '- ' + item.city : '' }}</span>
            </div>
            
            <div class="prompt-text">
              {{ truncateText(item.text, 150) }}
            </div>
            
            <!-- Role selector -->
            <div class="prompt-role" v-if="editingId === item.id">
              <Dropdown
                v-model="editingPrompt.role_id"
                :options="roles"
                optionLabel="name"
                optionValue="id"
                placeholder="Selecione o papel"
                class="w-full"
              />
            </div>
            <div class="prompt-role" v-else-if="getRoleName(item.role_id)">
              <i class="pi pi-user mr-2"></i>
              <span>{{ getRoleName(item.role_id) }}</span>
            </div>
          </div>
          
          <div class="prompt-card-actions">
            <!-- Modo de edição -->
            <template v-if="editingId === item.id">
              <Button
                label="Salvar"
                icon="pi pi-check"
                class="p-button-success p-button-sm"
                @click="saveEdit(item)"
              />
              <Button
                label="Cancelar"
                icon="pi pi-times"
                class="p-button-secondary p-button-sm"
                @click="cancelEdit"
              />
            </template>
            
            <!-- Modo normal -->
            <template v-else>
              <Button
                icon="pi pi-eye"
                class="p-button-text p-button-sm"
                :disabled="isProcessing(item)"
                @click="openDetailsModal(item)"
                tooltip="Ver detalhes"
              />
              <Button
                icon="pi pi-pencil"
                class="p-button-text p-button-sm"
                :disabled="isProcessing(item)"
                @click="editInsight(item)"
                tooltip="Editar"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-text p-button-sm p-button-danger"
                :disabled="isProcessing(item)"
                @click="confirmDelete(item)"
                tooltip="Excluir"
              />
            </template>
          </div>
        </div>
      </div>

      <!-- Layout criativo para quando não houver prompts -->
      <div v-else class="empty-prompts-container">
        <div class="empty-prompts-content">
          <i class="fas fa-lightbulb fa-4x pulse-animation"></i>
          <div class="empty-prompts-text">
            <h3>Nenhum Prompt Encontrado</h3>
            <p>Parece que ainda não existem prompts configurados para insights. Crie seu primeiro prompt para começar a explorar ideias inteligentes para o seu destino.</p>
          </div>
          <Button
            label="Criar"
            icon="pi pi-plus-circle"
            class="p-button-primary p-button-rounded"
            @click="openConfigModal"
          />
        </div>
      </div>
    </Dialog>

    <!-- Diálogo de confirmação de exclusão -->
    <Dialog
      :visible.sync="showDeleteDialog"
      modal
      :style="{width: '450px'}"
      header="Confirmar Exclusão"
    >
      <div class="confirmation-content">
        <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem; color: var(--yellow-700)" />
        <span>Tem certeza que deseja excluir este insight?</span>
      </div>
      <template #footer>
        <Button
          label="Não"
          icon="pi pi-times"
          class="p-button-text"
          @click="closeDeleteDialog"
        />
        <Button
          label="Sim"
          icon="pi pi-check"
          class="p-button-danger"
          @click="executeDelete"
        />
      </template>
    </Dialog>

    <Dialog
      :visible.sync="showDetailsModal"
      modal
      :style="{width: '80vw'}"
      :breakpoints="{'960px': '75vw', '640px': '90vw'}"
      :header="selectedPrompt ? selectedPrompt.title : 'Detalhes do Prompt'"
    >
      <div v-if="selectedPrompt" class="prompt-details-container">
        <TabView>
          <!-- Tab de Personas -->
          <TabPanel header="Personas">
            <div class="personas-container">
              <div v-if="selectedPrompt.debate_data && selectedPrompt.debate_data.personas && selectedPrompt.debate_data.personas.length > 0" class="personas-horizontal-scroll">
                <div class="personas-row">
                  <div 
                    v-for="persona in selectedPrompt.debate_data.personas" 
                    :key="persona.id" 
                    class="persona-card-enhanced"
                    :style="{ borderColor: persona.color || '#e9ecef' }"
                  >
                    <div class="persona-header" :style="{ backgroundColor: persona.color || '#e9ecef' }">
                      <div class="persona-avatar-container">
                        <img 
                          v-if="persona.avatar" 
                          :src="persona.avatar" 
                          :alt="persona.name" 
                          class="persona-avatar"
                        />
                        <i v-else class="pi pi-user"></i>
                      </div>
                      <h4 class="persona-name">{{ persona.name }}</h4>
                    </div>
                    <div class="persona-body">
                      <div class="persona-role" v-if="persona.role">
                        <span class="role-label">Papel:</span> {{ persona.role }}
                      </div>
                      <div class="persona-description">
                        {{ persona.description }}
                      </div>
                      <div class="persona-model" v-if="persona.model_id">
                        <span class="model-label">Modelo:</span> {{ persona.model_id }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="no-personas">
                <i class="pi pi-users"></i>
                <p>Nenhuma persona selecionada</p>
              </div>
            </div>
          </TabPanel>

          <!-- Tab de Debate -->
          <TabPanel header="Debate">
            <div class="debate-container" v-if="selectedPrompt.debate_data">
              <!-- Métricas do Debate -->
              <div class="debate-metrics">
                <div class="metric-card">
                  <i class="pi pi-key"></i>
                  <span class="metric-value">{{ selectedPrompt.debate_data.total_tokens || 0 }}</span>
                  <span class="metric-label">Tokens</span>
                </div>
                <div class="metric-card">
                  <i class="pi pi-comments"></i>
                  <span class="metric-value">{{ selectedPrompt.debate_data.total_messages || 0 }}</span>
                  <span class="metric-label">Mensagens</span>
                </div>
              </div>

              <!-- Timeline do Debate -->
              <div class="debate-timeline" v-if="selectedPrompt.debate_data && selectedPrompt.debate_data.timeline && selectedPrompt.debate_data.timeline.length">
                <div v-for="(item, index) in selectedPrompt.debate_data.timeline" :key="index" class="message-card">
                  <div class="message-header">
                    <div class="timeline-item-persona" :style="getPersonaStyle(item.persona_id)">
                      <img v-if="getPersonaAvatar(item.persona_id)" :src="getPersonaAvatar(item.persona_id)" class="timeline-item-persona-avatar" />
                      <div v-else class="timeline-item-persona-avatar-placeholder">
                        {{ getPersonaInitials(item.persona_name || 'Sistema') }}
                      </div>
                      <div class="timeline-item-persona-name">{{ getPersonaName(item.persona_id, item.persona_name) }}</div>
                    </div>
                  </div>
                  
                  <div class="timeline-item-content">
                    <TabView class="timeline-tabs">
                      <TabPanel header="Response">
                        <pre v-if="item.content" class="timeline-item-content-text">{{ item.content }}</pre>
                        <pre v-else-if="item.response" class="timeline-item-content-text">{{ item.response }}</pre>
                        <pre v-else-if="item.agent_response" class="timeline-item-content-text">{{ item.agent_response }}</pre>
                        <pre v-else class="timeline-item-content-text text-gray-500">Sem conteúdo disponível</pre>
                      </TabPanel>
                      <TabPanel header="Prompt">
                        <pre v-if="item.prompt_input" class="timeline-item-content-text">{{ formatPromptInput(item.prompt_input) }}</pre>
                        <pre v-else class="timeline-item-content-text text-gray-500">Prompt não disponível</pre>
                      </TabPanel>
                    </TabView>
                    
                    <div class="timeline-item-meta">
                      <div class="timeline-item-meta-item">
                        <span class="timeline-item-meta-label">Modelo:</span>
                        <span class="timeline-item-meta-value">{{ item.model_name || 'N/A' }}</span>
                      </div>
                      <div class="timeline-item-meta-item">
                        <span class="timeline-item-meta-label">Tempo:</span>
                        <span class="timeline-item-meta-value">{{ formatResponseTime(item.response_time) }}</span>
                      </div>
                      <div class="timeline-item-meta-item" v-if="item.temperature !== undefined">
                        <span class="timeline-item-meta-label">Temperature:</span>
                        <span class="timeline-item-meta-value">{{ item.temperature || 0 }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="no-debate">
                <i class="pi pi-comments"></i>
                <p>Nenhum debate iniciado</p>
              </div>
            </div>
          </TabPanel>
          
          <!-- Nova Tab de Consensus -->
          <TabPanel header="Consensus">
            <div class="consensus-container" v-if="selectedPrompt.debate_id">
              <div v-if="selectedPrompt.consensus_data">
                <!-- Card principal com o resumo do consensus -->
                <div class="consensus-header-card">
                  <div class="consensus-score-visual">
                    <div class="score-ring" :class="consensusLevelClass">
                      <div class="score-value">{{ selectedPrompt.consensus_data.consensus_score }}%</div>
                    </div>
                    <div class="score-label">
                      <h3>{{ selectedPrompt.consensus_data.consensus_level }}</h3>
                      <p>Nível de Consenso</p>
                    </div>
                  </div>
                  <div class="consensus-meta">
                    <div class="meta-item">
                      <i class="pi pi-calendar"></i>
                      <span>{{ formatDate(selectedPrompt.consensus_data.timestamp) }}</span>
                    </div>
                    <div class="meta-item">
                      <i class="pi pi-cog"></i>
                      <span>Modelo: {{ selectedPrompt.consensus_data.model_id }}</span>
                    </div>
                    <div class="meta-item">
                      <i class="pi pi-clock"></i>
                      <span>Tempo de análise: {{ (selectedPrompt.consensus_data.analysis_time_ms / 1000).toFixed(1) }}s</span>
                    </div>
                  </div>
                </div>
                
                <!-- Síntese do consensus -->
                <div class="consensus-synthesis-card">
                  <h3><i class="pi pi-star"></i> Síntese do Consenso</h3>
                  <div class="consensus-synthesis">
                    <p v-if="selectedPrompt.consensus_data.function_response && selectedPrompt.consensus_data.function_response.consensus_response">
                      {{ selectedPrompt.consensus_data.function_response.consensus_response.replace('**(Alto Consenso)**', '') }}
                    </p>
                  </div>
                </div>
                
                <div class="consensus-points-container">
                  <!-- Pontos de Consenso -->
                  <div class="consensus-points-card">
                    <h3><i class="pi pi-check-circle"></i> Pontos de Consenso</h3>
                    <div class="points-list">
                      <div v-for="(point, index) in selectedPrompt.consensus_data.points_of_consensus" :key="'consensus-'+index" class="point-item point-consensus">
                        <div class="point-number">{{ index + 1 }}</div>
                        <div class="point-content">{{ point }}</div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Pontos de Divergência -->
                  <div class="consensus-points-card">
                    <h3><i class="pi pi-exclamation-triangle"></i> Pontos de Divergência</h3>
                    <div class="points-list">
                      <div v-for="(point, index) in selectedPrompt.consensus_data.points_of_divergence" :key="'divergence-'+index" class="point-item point-divergence">
                        <div class="point-number">{{ index + 1 }}</div>
                        <div class="point-content">{{ point }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
        </TabView>
      </div>
    </Dialog>

    <Dialog
      :visible.sync="showPersonasModal"
      modal
      :style="{width: '70vw'}"
      header="Gerenciamento de Personas"
    >
        <!-- Visualização de Personas -->
        <div v-if="!editingPersona" class="personas-list-view">
          <div class="personas-header">
            <h2>Personas Disponíveis</h2>
            <div class="personas-actions">
              <Button
                label="Nova Persona"
                icon="pi pi-plus"
                class="p-button-primary mr-2"
                @click="createNewPersona"
              />
              <Button
                label="Criar com IA"
                icon="pi pi-star-fill"
                class="p-button-success"
                @click="openCreateWithAI"
              />
            </div>
          </div>
          
          <div v-if="personas.length > 0" class="personas-grid">
            <div v-for="persona in personas" :key="persona.id" :class="['persona-list-card', { 'skeleton-card': persona.id === skeletonPersonaId }]">
              <!-- Card de Skeleton Loading -->
              <template v-if="persona.id === skeletonPersonaId">
                <div class="persona-list-avatar skeleton-avatar">
                  <div class="skeleton-pulse"></div>
                </div>
                <div class="persona-list-info">
                  <div class="skeleton-name skeleton-pulse"></div>
                  <div class="skeleton-role skeleton-pulse"></div>
                  <div class="persona-tags">
                    <div class="skeleton-tag skeleton-pulse"></div>
                    <div class="skeleton-tag skeleton-pulse"></div>
                  </div>
                </div>
                <div class="persona-list-actions">
                  <div class="skeleton-action skeleton-pulse"></div>
                  <div class="skeleton-action skeleton-pulse"></div>
                </div>
              </template>
              
              <!-- Card Normal -->
              <template v-else>
                <div class="persona-list-avatar" :style="{ backgroundColor: persona.color || '#e9ecef' }">
                  <img v-if="persona.avatar" :src="persona.avatar" alt="Avatar" />
                  <i v-else class="pi pi-user"></i>
                </div>
                <div class="persona-list-info">
                  <h3>{{ persona.name }}</h3>
                  <p class="persona-role">{{ persona.role }}</p>
                  <div class="persona-tags">
                    <span v-if="persona.expertises && persona.expertises.length" class="expertise-tag">
                      {{ persona.expertises[0].name }}
                      <span v-if="persona.expertises.length > 1">+{{ persona.expertises.length - 1 }}</span>
                    </span>
                    <span v-if="persona.traits && persona.traits.length" class="trait-tag">
                      {{ persona.traits[0].name }}
                      <span v-if="persona.traits.length > 1">+{{ persona.traits.length - 1 }}</span>
                    </span>
                  </div>
                </div>
                <div class="persona-list-actions">
                  <Button
                    icon="pi pi-pencil"
                    class="p-button-text p-button-rounded"
                    @click="editPersona(persona)"
                    tooltip="Editar"
                  />
                  <Button
                    v-if="!['consensus_builder', 'consensus_init', 'consensus_reviewer', 'reviews_consult', 'social_media_consult', 'reviews_common_problems_analyst', 'reviews_keywords_analyst'].includes(persona.id)"
                    icon="pi pi-trash"
                    class="p-button-text p-button-rounded p-button-danger"
                    @click="confirmDeletePersona(persona)"
                    tooltip="Excluir"
                  />
                </div>
              </template>
            </div>
          </div>
          
          <!-- Layout criativo para quando não houver personas -->
          <div v-else class="empty-personas-container">
            <div class="empty-personas-content">
              <i class="fas fa-users fa-4x pulse-animation"></i>
              <div class="empty-personas-text">
                <h3>Nenhuma Persona Encontrada</h3>
                <p>Crie sua primeira persona para começar a enriquecer os debates e insights do seu destino.</p>
              </div>
              <Button
                label="Criar Persona"
                icon="pi pi-plus-circle"
                class="p-button-primary p-button-rounded"
                @click="createNewPersona"
              />
            </div>
          </div>
        </div>
        
        <!-- Formulário de Edição de Persona -->
        <div v-else class="persona-form">Gerenciamento de Personas

          <h2>{{ isNewPersona ? 'Nova Persona' : 'Editar Persona' }}</h2>
          
          <div class="p-grid form-grid">
            <div class="p-col-12 p-md-3 avatar-section">
              <div 
                class="avatar-preview" 
                :style="{ backgroundColor: editingPersona.color || '#e9ecef' }"
                @click="triggerFileInput"
              >
                <img 
                  v-if="avatarPreview" 
                  :src="avatarPreview" 
                  :alt="editingPersona.name" 
                  class="persona-avatar"
                />
                <i v-else-if="editingPersona.avatar" class="pi pi-user"></i>
                <i v-else class="pi pi-camera"></i>
                <input 
                  type="file" 
                  ref="fileInput" 
                  style="display: none"
                  accept=".jpg,.jpeg,image/jpeg" 
                  @change="handleFileUpload" 
                />
              </div>
              
              <ColorPicker 
                v-model="editingPersona.color" 
                class="color-picker" 
                :style="{ marginTop: '1rem' }"
              />
            </div>
            
            <div class="p-col-12 p-md-9 form-fields">
              <div class="p-grid nested-grid">
                <div class="p-col-12 p-md-6 field">
                  <label for="persona-id">ID da Persona*</label>
                  <InputText 
                    id="persona-id" 
                    v-model="editingPersona.id" 
                    class="w-full"
                    :disabled="!isNewPersona"
                    placeholder="Ex: economist"
                  />
                  <small class="helper-text">Identificador único usado pelo sistema (sem espaços)</small>
                </div>
                
                <div class="p-col-12 p-md-6 field">
                  <label for="persona-name">Nome da Persona*</label>
                  <InputText 
                    id="persona-name" 
                    v-model="editingPersona.name" 
                    class="w-full"
                    placeholder="Ex: Economista"
                  />
                </div>
                
                <div class="p-col-12 p-md-6 field">
                  <label for="persona-role">Função/Papel*</label>
                  <InputText 
                    id="persona-role" 
                    v-model="editingPersona.role" 
                    class="w-full"
                    placeholder="Ex: Analista econômico"
                  />
                </div>
                
                <div class="p-col-12 p-md-6 field">
                  <label for="persona-model">Modelo de IA*</label>
                  <Dropdown 
                    id="persona-model" 
                    v-model="editingPersona.model_id" 
                    :options="aiModels" 
                    optionLabel="name" 
                    optionValue="id" 
                    class="w-full"
                    placeholder="Selecione um modelo"
                  />
                </div>
                
                <div class="p-col-12 field">
                  <label for="persona-description">Descrição*</label>
                  <Textarea 
                    id="persona-description" 
                    v-model="editingPersona.description" 
                    rows="3" 
                    class="w-full"
                    placeholder="Descreva o papel e características desta persona"
                  />
                </div>
                
                <div class="p-col-12 field">
                  <label for="persona-system-prompt">System Prompt*</label>
                  <Textarea 
                    id="persona-system-prompt" 
                    v-model="editingPersona.system_prompt" 
                    rows="5" 
                    class="w-full"
                    placeholder="Instruções para o modelo de IA"
                  />
                </div>
                
                <div class="p-col-12 p-md-6 field">
                  <label>Expertises</label>
                  <MultiSelect
                    v-model="selectedExpertises"
                    :options="availableExpertises"
                    optionLabel="name"
                    placeholder="Selecione expertises"
                    :filter="true"
                    :showToggleAll="false"
                    class="w-full multiselect-field"
                  >
                    <template #option="slotProps">
                      <div class="multiselect-item">
                        <span>{{ slotProps.option.name }}</span>
                      </div>
                    </template>
                    <template #value="slotProps">
                      <div class="expertise-token-container">
                        <div v-for="option of slotProps.value" :key="option.id" class="multiselect-token">
                          {{ option.name }}
                          <Button 
                            icon="pi pi-trash" 
                            class="p-button-rounded p-button-danger p-button-text token-remove-btn"
                            @click.stop="removeExpertiseFromPersona(option)"
                            aria-label="Remover expertise" 
                          />
                        </div>
                        <placeholder v-if="!slotProps.value || slotProps.value.length === 0">
                          Selecione expertises
                        </placeholder>
                      </div>
                    </template>
                  </MultiSelect>
                  <div class="add-new-item">
                    <InputText v-model="newExpertise" placeholder="Nova expertise" class="w-full" />
                    <Button icon="pi pi-plus" class="p-button-sm" @click="addNewExpertise" />
                  </div>
                </div>
                
                <div class="p-col-12 p-md-6 field">
                  <label>Traços de Personalidade</label>
                  <MultiSelect
                    v-model="selectedTraits"
                    :options="availableTraits"
                    optionLabel="name"
                    placeholder="Selecione traços"
                    :filter="true"
                    :showToggleAll="false"
                    class="w-full multiselect-field"
                  >
                    <template #option="slotProps">
                      <div class="multiselect-item">
                        <span>{{ slotProps.option.name }}</span>
                      </div>
                    </template>
                    <template #value="slotProps">
                      <div class="expertise-token-container">
                        <div v-for="option of slotProps.value" :key="option.id" class="multiselect-token">
                          {{ option.name }}
                          <Button 
                            icon="pi pi-trash" 
                            class="p-button-rounded p-button-danger p-button-text token-remove-btn"
                            @click.stop="removeTraitFromPersona(option)"
                            aria-label="Remover traço de personalidade" 
                          />
                        </div>
                        <placeholder v-if="!slotProps.value || slotProps.value.length === 0">
                          Selecione traços
                        </placeholder>
                      </div>
                    </template>
                  </MultiSelect>
                  <div class="add-new-item">
                    <InputText v-model="newTrait" placeholder="Novo traço" class="w-full" />
                    <Button icon="pi pi-plus" class="p-button-sm" @click="addNewTrait" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="form-actions">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              class="p-button-text"
              @click="cancelEditPersona"
            />
            <Button
              label="Salvar"
              icon="pi pi-check"
              class="p-button-primary"
              @click="savePersona"
            />
          </div>
        </div>
      
      <!-- Diálogo de confirmação de exclusão de persona -->
      <Dialog
        :visible.sync="showDeletePersonaDialog"
        modal
        :style="{width: '450px'}"
        header="Confirmar Exclusão"
      >
        <div class="confirmation-content">
          <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem; color: var(--yellow-700)" />
          <span>Tem certeza que deseja excluir esta persona?</span>
        </div>
        <template #footer>
          <Button
            label="Não"
            icon="pi pi-times"
            class="p-button-text"
            @click="closeDeletePersonaDialog"
          />
          <Button
            label="Sim"
            icon="pi pi-check"
            class="p-button-danger"
            @click="executeDeletePersona"
          />
        </template>
      </Dialog>
    </Dialog>

    <!-- Modal de edição usando o InsightsConfigModal -->
    <Dialog
      :visible.sync="showEditModal"
      modal
      :style="{width: '950px'}"
      header="Editar Insight"
    >
      <div v-if="!editingPrompt" class="loading-container">
        <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
        <p>Carregando dados do insight...</p>
      </div>
      <div v-else-if="editingPrompt && Object.keys(editingPrompt).length === 0" class="error-container">
        <i class="pi pi-exclamation-triangle" style="font-size: 2rem; color: var(--red-500)"></i>
        <p>Erro ao carregar dados do insight.</p>
      </div>
      <InsightsConfigModal
        v-else
        :showConfigModal="true"
        :module="editingPrompt.module"
        :moduleId="editingPrompt.module_id !== undefined && editingPrompt.module_id !== null ? parseInt(editingPrompt.module_id) : null"
        :states="states"
        :roles="roles"
        :editMode="true"
        :editData="editingPrompt"
        @update:showConfigModal="closeEditModal"
        @save="handleEditSave"
      />
    </Dialog>

    <!-- Full Text Dialog -->
    <Dialog 
      :visible.sync="showFullTextDialog" 
      :style="{width: '80vw'}" 
      modal
      :header="fullTextDialogTitle"
    >
      <div class="full-text-content" v-html="formatMarkdown(fullTextContent)"></div>
    </Dialog>

    <!-- Diálogo de criação com IA -->
    <Dialog
      :visible.sync="showCreateWithAIDialog"
      modal
      :style="{width: '550px'}"
      header="Criar Persona com IA"
    >
      <div v-if="!isLoadingAIPersona" class="create-with-ai-content">
        <h3>Descreva a persona que você deseja criar</h3>
        <p>Detalhe características, especialidades e função desta persona para que a IA possa criar um perfil completo.</p>
        
        <div class="field">
          <Textarea 
            v-model="aiPersonaInput" 
            rows="5" 
            class="w-full"
            placeholder="Uma persona especialista em educação, com foco em metodologias inovadoras e avaliação de aprendizagem"
          />
        </div>
        
        <div class="dialog-footer">
          <Button
            label="Cancelar"
            icon="pi pi-times"
            class="p-button-text"
            @click="closeCreateWithAIDialog"
          />
          <Button
            label="Criar"
            icon="pi pi-check"
            class="p-button-primary"
            @click="generatePersonaWithAI"
            :disabled="!aiPersonaInput.trim()"
          />
        </div>
      </div>
      
      <div v-else class="ai-persona-skeleton">
        <div class="skeleton-header">
          <div class="skeleton-avatar"></div>
          <div class="skeleton-title"></div>
        </div>
        
        <div class="skeleton-fields">
          <div class="skeleton-field"></div>
          <div class="skeleton-field"></div>
          <div class="skeleton-field"></div>
          <div class="skeleton-field"></div>
          <div class="skeleton-field skeleton-text-area"></div>
          <div class="skeleton-field skeleton-text-area"></div>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'
import SelectInput from '@/Shared/SelectInput'
import StateCitySelector from '@/Pages/Reviews/Components/StateCitySelector/StateCitySelector.vue'
import InsightsConfigModal from './InsightsConfigModal.vue'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Dropdown from 'primevue/dropdown'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import ColorPicker from 'primevue/colorpicker'
import MultiSelect from 'primevue/multiselect'
import axios from 'axios'
import { useStateCityStore } from '@/Stores/stateCityStore'

export default {
  name: 'InsightsAdmin',
  components: {
    Accordion,
    AccordionTab,
    Multiselect,
    SelectInput,
    StateCitySelector,
    InsightsConfigModal,
    Button,
    Dialog,
    InputText,
    Textarea,
    Dropdown,
    TabView,
    TabPanel,
    ColorPicker,
    MultiSelect
  },
  props: {
    module: {
      type: String,
      default: ''
    },
    roleIdProp: {
      type: Number,
      required: true
    },
    prompts: {
      type: Array,
      default: () => []
    },
    roles: {
      type: Array,
      default: () => []
    },
    states: {
      type: Array,
      default: () => []
    }
  },
  data() {
    const store = useStateCityStore()
    return {
      store,
      showConfigModal: false,
      showInsightsModal: false,
      showPersonasModal: false,
      showEditModal: false,
      editingPrompt: null,
      newPrompt: {
        title: '',
        text: '',
        state: '',
        city: ''
      },
      statusPollingIntervals: {},
      editingId: null,
      showDeleteDialog: false,
      promptToDelete: null,
      selectedPersonas: [],
      debatePersonas: [],
      debateMessages: [],
      debateMetrics: null,
      loadingDebate: false,
      updatingPersonas: false,
      availablePersonas: [
        { name: 'Turista Internacional', id: 'international_tourist' },
        { name: 'Turista Nacional', id: 'national_tourist' },
        { name: 'Gestor Público', id: 'public_manager' },
        { name: 'Empresário Local', id: 'local_entrepreneur' },
        { name: 'Especialista em Turismo', id: 'tourism_expert' },
        { name: 'Morador Local', id: 'local_resident' },
        { name: 'Agente de Viagens', id: 'travel_agent' },
        { name: 'Analista de Dados', id: 'data_analyst' }
      ],
      showDebateDetails: false,
      selectedDebateData: {
        messages: [],
        personas: [],
        modelStats: {}
      },
      showFullTextDialog: false,
      fullTextContent: '',
      fullTextDialogTitle: '',
      editingPersona: null,
      isNewPersona: true,
      personas: [],
      aiModels: [],
      selectedExpertises: [],
      newExpertise: '',
      availableExpertises: [],
      selectedTraits: [],
      newTrait: '',
      availableTraits: [],
      showDeletePersonaDialog: false,
      personaToDelete: null,
      avatarPreview: null,
      isLoadingAIPersona: false,
      aiPersonaInput: '',
      showCreateWithAIDialog: false,
      skeletonPersonaId: 'skeleton-loading', // ID para a persona em skeleton loading
      showDetailsModal: false,
      selectedPrompt: null,
      socialMediaDataSourceOptions: [
        { id: 'trending_posts', label: 'Postagens em alta' },
        { id: 'demographic_map', label: 'Mapa de distribuição demográfica' },
        { id: 'tourist_spots', label: 'Pontos turísticos mais citados' },
        { id: 'trending_topics', label: 'Assuntos do momento' },
        { id: 'relevant_events', label: 'Eventos relevantes' },
        { id: 'trending_keywords', label: 'Palavras-chave em alta' },
        { id: 'top_influencers', label: 'Top Influenciadores' },
        { id: 'word_cloud', label: 'Nuvem de palavras' }
      ]
    }
  },
  created() {
    // Carregar modelos AI disponíveis
    this.loadAIModels();
  },
  methods: {
    // Ajusta a cor para torná-la mais clara ou escura
    adjustColor(color, percent) {
      if (!color) return '#e9ecef';
      
      // Verifica se a cor está no formato hexadecimal
      if (!color.startsWith('#')) {
        return color;
      }
      
      let R = parseInt(color.substring(1,3), 16);
      let G = parseInt(color.substring(3,5), 16);
      let B = parseInt(color.substring(5,7), 16);

      R = parseInt(R * (100 + percent) / 100);
      G = parseInt(G * (100 + percent) / 100);
      B = parseInt(B * (100 + percent) / 100);

      R = (R < 255) ? R : 255;  
      G = (G < 255) ? G : 255;  
      B = (B < 255) ? B : 255;  

      const RR = ((R.toString(16).length === 1) ? '0' + R.toString(16) : R.toString(16));
      const GG = ((G.toString(16).length === 1) ? '0' + G.toString(16) : G.toString(16));
      const BB = ((B.toString(16).length === 1) ? '0' + B.toString(16) : B.toString(16));

      return '#' + RR + GG + BB;
    },
    
    // Encontra a persona pelo ID na lista de personas
    findPersonaById(personaId, personasList) {
      if (!personaId || !personasList) return null;
      return personasList.find(p => p && p.id === personaId) || null;
    },
    
    // Obtém o estilo para a badge da persona baseado no ID
    getPersonaStyle(personaId, personasList) {
      if (!personaId || !personasList) return {};
      const persona = this.findPersonaById(personaId, personasList);
      if (persona && persona.color) {
        return { backgroundColor: persona.color };
      }
      // Cor padrão para personas não encontradas
      return { backgroundColor: '#6366F1' };
    },
    
    // Obtém o avatar da persona baseado no ID
    getPersonaAvatar(personaId, personasList) {
      if (!personaId || !personasList) return null;
      const persona = this.findPersonaById(personaId, personasList);
      return persona && persona.avatar ? persona.avatar : null;
    },
    
    // Obtém o nome da persona baseado no ID
    getPersonaName(personaId, personasList) {
      if (!personaId || !personasList) return 'Sistema';
      const persona = this.findPersonaById(personaId, personasList);
      return persona && persona.name ? persona.name : (personaId || 'Sistema');
    },
    // Carregar modelos de IA da API
    async loadAIModels() {
      try {
        const response = await axios.get('/api/ia-models/active');

        // Verificar se recebemos dados válidos
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          this.aiModels = response.data.map(model => ({
            id: model.api_model_name,
            name: `${model.name} - Inp($${model.input_cost})/Out(${model.output_cost})`,
            original: model
          }));
        } else {
          throw new Error('Nenhum modelo de IA ativo encontrado');
        }
      } catch (error) {
        console.error('Erro ao carregar modelos de IA:', error);
        console.error('Detalhes do erro:', error.response?.status, error.response?.data);
        
        // Fallback para modelos fixos caso ocorra erro
        this.aiModels = [
          { id: 'gpt-4', name: 'GPT-4' },
          { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo' },
          { id: 'claude-3-opus-20240229', name: 'Claude 3 Opus - Inp($15.00)/Out($75.00)' },
          { id: 'claude-3-sonnet-20240229', name: 'Claude 3 Sonnet - Inp($3.00)/Out($15.00)' },
          { id: 'claude-3-haiku-20240307', name: 'Claude 3 Haiku - Inp($0.25)/Out($1.25)' }
        ];
        console.log('Usando modelos de fallback:', this.aiModels);
        
        // Exibir mensagem de erro apenas se não for um erro de rede (pode ser ambiente offline)
        if (error.response) {
          this.showToastError({ message: 'Erro ao carregar modelos de IA. Utilizando valores padrão.' });
        }
      }
    },
    openConfigModal() {
      this.showConfigModal = true
    },
    openInsightsModal() {
      this.showInsightsModal = true
    },
    openPersonasModal() {
      this.showPersonasModal = true;
      
      // Carregar dados para o gerenciador de personas
      this.loadPersonas();
      this.loadExpertises();
      this.loadTraits();
    },
    closeEditModal() {
      this.showEditModal = false
      this.editingPrompt = null
    },
    editPrompt(prompt) {
      this.editingPrompt = { ...prompt }
      this.showEditModal = true
    },
    savePrompt() {
      // Validar os campos obrigatórios
      if (!this.editingPrompt.title || !this.editingPrompt.text || !this.editingPrompt.role_id) {
        this.showToastError({ message: 'Por favor, preencha todos os campos obrigatórios' });
        return;
      }

      if (this.editingPrompt) {
        this.updatePrompt(this.editingPrompt);
      } else {
        this.createPrompt();
      }
      this.closeEditModal();
      this.showToastSuccess('Prompt salvo com sucesso');
    },
    handleModalVisibility(value) {
      this.showConfigModal = value
    },
    handleConfigSave(config) {
      this.$emit('create-prompt', config)
    },
    showToastSuccess(message) {
      this.$toast.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: message,
        life: 3000
      })
    },
    showToastError(error) {
      const message = error.response?.data?.message || error.message || 'Erro desconhecido';
      this.$toast.add({
        severity: 'error',
        summary: 'Erro',
        detail: message,
        life: 5000
      });
    },
    getPrompts() {
      this.$emit('get-prompts')
    },
    updatePrompt(prompt) {
      this.$emit('update-prompt', prompt)
    },
    createPrompt() {
      this.$emit('create-prompt')
    },
    deletePrompt(promptId) {
      this.$emit('delete-prompt', promptId)
    },
    getStatusClass(status) {
      switch (status) {
        case 'completed':
          return 'status-completed'
        case 'processing':
          return 'status-processing'
        case 'pending':
          return 'status-pending'
        case 'failed':
          return 'status-failed'
        default:
          return 'status-unknown'
      }
    },
    getStatusLabel(status) {
      switch (status) {
        case 'completed':
          return 'Concluído'
        case 'processing':
          return 'Processando'
        case 'pending':
          return 'Pendente'
        case 'failed':
          return 'Falhou'
        default:
          return 'Desconhecido'
      }
    },
    isProcessing(item) {
      return item.status === 'processing';
    },
    getRoleName(roleId) {
      const role = this.roles.find(r => r.id === roleId)
      return role ? role.name : ''
    },
    startEdit(item) {
      this.editingId = item.id;
      this.editingPrompt = { ...item };
    },
    saveEdit(item) {
      this.updatePrompt(this.editingPrompt);
      this.editingId = null;
      this.editingPrompt = null;
    },
    cancelEdit() {
      this.editingId = null;
      this.editingPrompt = null;
    },
    confirmDelete(item) {
      this.promptToDelete = item;
      this.showDeleteDialog = true;
    },
    closeDeleteDialog() {
      this.showDeleteDialog = false;
      this.promptToDelete = null;
    },
    executeDelete() {
      this.deletePrompt(this.promptToDelete.id);
      this.closeDeleteDialog();
    },
    updateCity(city) {
      this.editingPrompt.city = city;
    },
    updateState(state) {
      this.editingPrompt.state = state;
    },
    async loadPromptDebate(prompt) {
      if (!prompt.debate_id) return;
      
      try {
        // Get debate details from the service usando a rota correta
        const response = await axios.get(this.route('indicators.debate.details', { debateId: prompt.debate_id }));
        const data = response.data;
        
        // Atualizar o estado com os dados do debate
        this.selectedDebateData = {
          messages: data.timeline,
          personas: data.personas,
          modelStats: data.model_statistics
        };
        
        // Atualizar o estado de exibição do debate nas tabs
        this.showDebateDetails = true;
      } catch (error) {
        console.error('Erro ao carregar detalhes do debate:', error);
        this.showToastError(error);
      }
    },
    
    async updatePersonas(prompt, selectedPersonas) {
      try {
        const response = await axios.put(`${this.promptsApiUrl}/${prompt.id}/personas`, {
          selectedPersonas
        });
        
        // Atualizar o prompt localmente com as novas personas
        const index = this.prompts.findIndex(p => p.id === prompt.id);
        if (index !== -1) {
          this.$set(this.prompts, index, {
            ...this.prompts[index],
            agents: response.data.agents
          });
        }
        
        this.showToastSuccess('Personas atualizadas com sucesso');
      } catch (error) {
        console.error('Erro ao atualizar personas:', error);
        this.showToastError(error);
      }
    },
    openDebateDetails(prompt) {  // Renamed from showDebateDetails to openDebateDetails
      if (!prompt.debate_id || !prompt.debate_data) return;
      
      this.selectedDebateData = {
        messages: prompt.debate_data.timeline,
        personas: prompt.debate_data.personas,
        modelStats: prompt.debate_data.metrics
      };
      
      this.showDebateDetails = true;
    },
    
    async updateSelectedPersonas() {
      if (!this.currentPrompt) return;
      
      this.updatingPersonas = true;
      try {
        await this.updatePersonas(
          this.currentPrompt,
          this.selectedPersonas.map(p => p.id)
        );
      } catch (error) {
        console.error('Erro ao atualizar personas:', error);
        this.showToastError(error);
      } finally {
        this.updatingPersonas = false;
      }
    },
    async viewDebateDetails(debateId) {
      try {
        // Get debate details from the service
        const response = await axios.get(this.route('indicators.debate.details', { debateId }));
        const data = response.data;
        
        this.selectedDebateData = {
          messages: data.timeline,
          personas: data.personas,
          modelStats: data.model_statistics
        };
        
        this.showDebateDetails = true;
      } catch (error) {
        this.showToastError(error);
      }
    },
    truncateText(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.slice(0, length) + '...';
    },
    showFullText(text) {
      this.fullTextContent = text;
      this.fullTextDialogTitle = 'Texto Completo';
      this.showFullTextDialog = true;
    },
    formatMarkdown(text) {
      // Aqui você pode usar uma biblioteca como marked para renderizar markdown
      return text.replace(/\n/g, '<br>');
    },
    getPersonaBadgeClass(personaId) {
      return personaId.toLowerCase();
    },
    getPersonaIcon(personaId) {
      const icons = {
        coordinator: 'pi pi-user',
        social_scientist: 'pi pi-users',
        economist: 'pi pi-chart-line'
      };
      return icons[personaId.toLowerCase()] || 'pi pi-user';
    },
    getPersonaTitle(persona) {     
      const titles = {
        coordinator: 'Coordenador',
        social_scientist: 'Cientista Social',
        economist: 'Economista'
      };
      return titles[persona.toLowerCase()] || persona;
    },
    getPersonaDescription(persona) {
      const descriptions = {
        coordinator: 'Responsável por coordenar e sintetizar o debate',
        social_scientist: 'Analisa aspectos sociais e comportamentais',
        economist: 'Avalia impactos econômicos e financeiros'
      };
      return descriptions[persona.toLowerCase()] || '';
    },
    createNewPersona() {
      // Garantir que os modelos de IA estejam carregados
      if (!this.aiModels || this.aiModels.length === 0) {
        this.loadAIModels();
      }
      
      this.editingPersona = {
        id: '',
        name: '',
        role: '',
        model_id: '',
        description: '',
        system_prompt: '',
        color: '#4caf50',
        avatar: '',
        avatarFile: null,
        expertises: [],
        traits: []
      };
      this.selectedExpertises = [];
      this.selectedTraits = [];
      this.avatarPreview = null;
      this.isNewPersona = true;
    },
    editPersona(persona) {
      // Garantir que os modelos de IA estejam carregados
      if (!this.aiModels || this.aiModels.length === 0) {
        this.loadAIModels();
      }
      
      this.editingPersona = { ...persona, avatarFile: null };
      this.selectedExpertises = persona.expertises || [];
      this.selectedTraits = persona.traits || [];
      this.avatarPreview = persona.avatar || null;
      this.isNewPersona = false;
    },
    confirmDeletePersona(persona) {
      console.log('Confirmando exclusão da persona:', persona);
      
      if (!persona || !persona.id) {
        this.showToastError({ message: 'Persona inválida para exclusão' });
        return;
      }
      
      this.personaToDelete = { ...persona }; // Cria uma cópia para evitar problemas de referência
      this.showDeletePersonaDialog = true;
    },
    closeDeletePersonaDialog() {
      this.showDeletePersonaDialog = false;
      this.personaToDelete = null;
    },
    cancelEditPersona() {
      this.editingPersona = null;
      this.selectedExpertises = [];
      this.selectedTraits = [];
      this.avatarPreview = null;
    },
    async executeDeletePersona() {
      try {
        console.log('Tentando excluir persona:', this.personaToDelete);
        if (!this.personaToDelete || !this.personaToDelete.id) {
          this.showToastError({ message: 'ID da persona não encontrado' });
          return;
        }
        
        const url = `/api/personas/${this.personaToDelete.id}`;
        console.log('URL de deleção:', url);
        
        const response = await axios.delete(url);
        console.log('Resposta da deleção:', response);
        
        // Atualiza a lista removendo a persona excluída
        this.personas = this.personas.filter(p => p.id !== this.personaToDelete.id);
        this.showToastSuccess('Persona excluída com sucesso');
      } catch (error) {
        console.error('Erro ao excluir persona:', error);
        let errorMsg = 'Erro ao excluir persona';
        
        if (error.response) {
          console.error('Status:', error.response.status);
          console.error('Data:', error.response.data);
          
          if (error.response.data && error.response.data.error) {
            errorMsg = error.response.data.error;
          }
        }
        
        this.showToastError({ message: errorMsg });
      } finally {
        this.closeDeletePersonaDialog();
      }
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        // Só aceita jpg/jpeg
        if (!['image/jpeg'].includes(file.type)) {
          this.showToastError({ message: 'Apenas arquivos JPG são permitidos.' });
          event.target.value = '';
          return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
          this.avatarPreview = e.target.result;
        };
        reader.readAsDataURL(file);
        
        // Armazenar o arquivo para upload posterior
        this.editingPersona.avatarFile = file;
      }
    },
    async loadPersonas() {
      try {
        const response = await axios.get('/api/personas');
        this.personas = response.data;
      } catch (error) {
        console.error('Erro ao carregar personas:', error);
        this.showToastError(error);
      }
    },
    async loadExpertises() {
      try {
        const response = await axios.get('/api/persona-expertises');
        this.availableExpertises = response.data;
      } catch (error) {
        console.error('Erro ao carregar expertises:', error);
        this.showToastError(error);
      }
    },
    async loadTraits() {
      try {
        const response = await axios.get('/api/persona-traits');
        this.availableTraits = response.data;
      } catch (error) {
        console.error('Erro ao carregar traços:', error);
        this.showToastError(error);
      }
    },
    addNewExpertise() {
      if (!this.newExpertise.trim()) return;
      
      const expertiseName = this.newExpertise.trim();
      
      // Verifica se já existe uma expertise com esse nome
      const existingExpertise = this.availableExpertises.find(
        e => e.name.toLowerCase() === expertiseName.toLowerCase()
      );
      
      if (existingExpertise) {
        // Se já existe, apenas adiciona à seleção se ainda não estiver selecionada
        if (!this.selectedExpertises.some(e => e.id === existingExpertise.id)) {
          this.selectedExpertises.push(existingExpertise);
        }
      } else {
        // Se não existe, cria um objeto temporário com ID temporário
        const tempExpertise = {
          id: 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
          name: expertiseName,
          isTemp: true // Marca como temporário para identificação posterior
        };
        
        // Adiciona à lista de disponíveis e selecionadas
        this.availableExpertises.push(tempExpertise);
        this.selectedExpertises.push(tempExpertise);
      }
      
      this.newExpertise = '';
    },
    addNewTrait() {
      if (!this.newTrait.trim()) return;
      
      const traitName = this.newTrait.trim();
      
      // Verifica se já existe um traço com esse nome
      const existingTrait = this.availableTraits.find(
        t => t.name.toLowerCase() === traitName.toLowerCase()
      );
      
      if (existingTrait) {
        // Se já existe, apenas adiciona à seleção se ainda não estiver selecionado
        if (!this.selectedTraits.some(t => t.id === existingTrait.id)) {
          this.selectedTraits.push(existingTrait);
        }
      } else {
        // Se não existe, cria um objeto temporário com ID temporário
        const tempTrait = {
          id: 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
          name: traitName,
          isTemp: true // Marca como temporário para identificação posterior
        };
        
        // Adiciona à lista de disponíveis e selecionadas
        this.availableTraits.push(tempTrait);
        this.selectedTraits.push(tempTrait);
      }
      
      this.newTrait = '';
    },
    async savePersona() {
      // Validação básica
      if (!this.editingPersona.id || !this.editingPersona.name || !this.editingPersona.role || 
          !this.editingPersona.model_id || !this.editingPersona.description || !this.editingPersona.system_prompt) {
        this.showToastError({ message: 'Por favor, preencha todos os campos obrigatórios' });
        return;
      }
      
      // Validação específica para o modelo de IA
      if (!this.editingPersona.model_id) {
        this.showToastError({ message: 'Por favor, selecione um modelo de IA' });
        return;
      }
      
      try {
        console.log("Salvando persona:", this.editingPersona);
        console.log("Expertises selecionadas:", this.selectedExpertises);
        console.log("Traits selecionados:", this.selectedTraits);
        
        // Preparar dados para envio
        const formData = new FormData();
        
        // Adicionar campos básicos
        Object.keys(this.editingPersona).forEach(key => {
          if (key !== 'avatarFile' && key !== 'expertises' && key !== 'traits' && key !== 'avatar' && key !== 'saved_to_db' && key !== 'db_error') {
            formData.append(key, this.editingPersona[key]);
          }
        });

        // Se avatar for string (URL existente) e não tiver novo arquivo, mantém o antigo
        if (this.editingPersona.avatar && typeof this.editingPersona.avatar === 'string' && !this.editingPersona.avatarFile) {
          formData.append('avatar_url', this.editingPersona.avatar);
        }
        
        // Adicionar arquivo de avatar apenas se tiver sido selecionado
        if (this.editingPersona.avatarFile) {
          formData.append('avatar', this.editingPersona.avatarFile);
        }
        // Verificar se a persona foi criada com IA (tem expertises/traits com IDs gerados no frontend)
const wasCreatedWithAI = this.editingPersona.expertises?.some(e => typeof e.id === 'string' && !e.id.match(/^\d+$/)) || 
                         this.editingPersona.traits?.some(t => typeof t.id === 'string' && !t.id.match(/^\d+$/));

        if (wasCreatedWithAI && !this.isNewPersona) {
          console.log("Persona criada com IA, tratando todas as expertises e traits como novas");
          
          // Obter todos os nomes de expertises e traits
          const allExpertiseNames = this.selectedExpertises.map(e => e.name);
          const allTraitNames = this.selectedTraits.map(t => t.name);
          
          // Enviar todos como novos
          formData.append('expertises', JSON.stringify([]));
          formData.append('traits', JSON.stringify([]));
          formData.append('new_expertises', JSON.stringify(allExpertiseNames));
          formData.append('new_traits', JSON.stringify(allTraitNames));
        } else {
          // Preparar expertises e traits para enviar
          // Separar itens temporários (novos) e permanentes (existentes)
          const tempExpertises = this.selectedExpertises.filter(e => e.isTemp);
          const permExpertises = this.selectedExpertises.filter(e => !e.isTemp);
          
          const tempTraits = this.selectedTraits.filter(t => t.isTemp);
          const permTraits = this.selectedTraits.filter(t => !t.isTemp);
          
          // Extrair IDs dos itens permanentes
          const expertiseIds = permExpertises.map(e => e.id);
          const traitIds = permTraits.map(t => t.id);
          
          // Extrair nomes dos itens temporários
          const newExpertiseNames = tempExpertises.map(e => e.name);
          const newTraitNames = tempTraits.map(t => t.name);
          
          console.log("IDs de expertises existentes:", expertiseIds);
          console.log("Nomes de novas expertises:", newExpertiseNames);
          console.log("IDs de traços existentes:", traitIds);
          console.log("Nomes de novos traços:", newTraitNames);
          
          // Adicionar expertises e traits como JSON
          formData.append('expertises', JSON.stringify(expertiseIds));
          formData.append('traits', JSON.stringify(traitIds));
          formData.append('new_expertises', JSON.stringify(newExpertiseNames));
          formData.append('new_traits', JSON.stringify(newTraitNames));
        }
        // Adicionar _method=PUT para o caso de atualização
        if (!this.isNewPersona) {
          formData.append('_method', 'PUT');
        }
        
        // Para depuração - mostrar todos os campos do FormData
        for (let [key, value] of formData.entries()) {
          console.log(`${key}: ${value}`);
        }
        
        let response;
        const headers = {
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json'
        };
        
        if (this.isNewPersona) {
          // Criar nova persona
          console.log("Enviando requisição para criar persona");
          response = await axios.post('/api/personas', formData, { headers });
          
          this.personas.push(response.data);
          this.showToastSuccess('Persona criada com sucesso');
        } else {
          // Atualizar persona existente usando POST com _method=PUT
          console.log(`Enviando requisição para atualizar persona ID: ${this.editingPersona.id}`);
          response = await axios.post(`/api/personas/${this.editingPersona.id}`, formData, { headers });
          
          console.log("Resposta da atualização:", response.data);
          
          // Atualizar a persona na lista
          const index = this.personas.findIndex(p => p.id === this.editingPersona.id);
          if (index !== -1) {
            this.$set(this.personas, index, response.data);
          }
          
          this.showToastSuccess('Persona atualizada com sucesso');
        }
        
        // Recarregar a lista para garantir a sincronização
        await this.loadPersonas();
        
        this.cancelEditPersona();
      } catch (error) {
        console.error('Erro ao salvar persona:', error);
        console.error('Detalhes do erro:', error.response?.data);
        this.showToastError(error);
      }
    },
    removeExpertiseFromPersona(expertise) {
      this.selectedExpertises = this.selectedExpertises.filter(e => e.id !== expertise.id);
    },
    removeTraitFromPersona(trait) {
      this.selectedTraits = this.selectedTraits.filter(t => t.id !== trait.id);
    },
    async generatePersonaWithAI() {
      // Armazena o valor do input antes de fechar o diálogo
      const personaInput = this.aiPersonaInput.trim();
      
      // Verifica se o input não está vazio
      if (!personaInput) {
        this.showToastError({ message: 'Por favor, descreva a persona que deseja criar.' });
        return;
      }
      
      this.isLoadingAIPersona = true;
      
      // Adiciona o skeleton loading card
      const skeletonPersona = {
        id: this.skeletonPersonaId,
        name: '',
        role: '',
        color: '',
        expertises: [],
        traits: []
      };
      
      // Fecha o diálogo de criação com IA e abre o modal de personas
      this.closeCreateWithAIDialog();
      this.showPersonasModal = true;
      
      // Adiciona o skeleton card ao array de personas
      this.personas.push(skeletonPersona);
      
      try {
        console.log('Enviando request para criar persona com IA:', { persona_input: personaInput });
        
        const response = await axios.post('/api/set-ia-consensus/personas/create', {
          persona_input: personaInput
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        console.log('Resposta completa da API:', response);
        
        // Remove o skeleton card
        const skeletonIndex = this.personas.findIndex(p => p.id === this.skeletonPersonaId);
        if (skeletonIndex !== -1) {
          this.personas.splice(skeletonIndex, 1);
        }
        
        // Processa as expertises e traços retornados pela API
        const personaData = response.data;
        
        // Garante que as expertises e traços estão disponíveis nas listas
        if (personaData.expertise && Array.isArray(personaData.expertise)) {
          // Converte a propriedade 'expertise' para 'expertises' para manter consistência
          personaData.expertises = personaData.expertise.map(name => {
            return { id: name.toLowerCase().replace(/\s+/g, '_'), name };
          });
          // Remove a propriedade original para evitar duplicação
          delete personaData.expertise;
        }
        
        if (personaData.traits && Array.isArray(personaData.traits)) {
          personaData.traits = personaData.traits.map(name => {
            return { id: name.toLowerCase().replace(/\s+/g, '_'), name };
          });
        }
        
        // Adiciona a persona real retornada pela API
        this.personas.push(personaData);
        this.showToastSuccess('Persona criada com sucesso');
      } catch (error) {
        console.error('Erro ao criar persona com IA:', error);
        console.error('Detalhes do erro:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
          headers: error.response?.headers
        });
        
        // Remove o skeleton card em caso de erro
        const skeletonIndex = this.personas.findIndex(p => p.id === this.skeletonPersonaId);
        if (skeletonIndex !== -1) {
          this.personas.splice(skeletonIndex, 1);
        }
        
        this.showToastError(error);
      } finally {
        this.isLoadingAIPersona = false;
      }
    },
    closeCreateWithAIDialog() {
      this.showCreateWithAIDialog = false;
      this.aiPersonaInput = '';
    },
    openPersonaEdit(persona) {
      this.resetPersonaForm();
      this.showPersonaDialog = true;
      this.editingPersona = true;
      this.selectedPersona = persona;
      this.syncFormWithPersona(persona);
    },
    openCreateWithAI() {
      this.showCreateWithAIDialog = true;
    },
    openDetailsModal(prompt) {
      if (!prompt) {
        console.error('Prompt inválido');
        return;
      }
      
      const debateData = prompt.debate_data || {};
      const metrics = debateData.metrics || {};
      
      this.selectedPrompt = {
        ...prompt,
        debate_data: {
          total_tokens: metrics.total_tokens || 0,
          total_messages: metrics.total_messages || 0,
          avg_time: metrics.average_response_time ? `${Math.round(metrics.average_response_time * 100) / 100}s` : '0s',
          personas: debateData.personas || [],
          timeline: debateData.timeline || []
        }
      };
      
      // Verificar se há dados de debate e timeline antes de abrir o modal
      if (this.selectedPrompt.debate_data && !this.selectedPrompt.debate_data.timeline) {
        this.selectedPrompt.debate_data.timeline = [];
      }
      
      if (this.selectedPrompt.debate_data && !this.selectedPrompt.debate_data.personas) {
        this.selectedPrompt.debate_data.personas = [];
      }
      
      setTimeout(() => {
        this.showDetailsModal = true;
      }, 100);
    },
    
    // Métodos auxiliares para exibição do consensus
    getConsensusLevelClass(level) {
      if (!level) return 'level-none';
      
      // Adaptado para suportar tanto os valores em inglês quanto em português
      const levelLower = level.toLowerCase();
      
      if (levelLower.includes('alto') || levelLower.includes('high')) {
        return 'level-high';
      } else if (levelLower.includes('médio') || levelLower.includes('medium')) {
        return 'level-medium';
      } else if (levelLower.includes('baixo') || levelLower.includes('low')) {
        return 'level-low';
      } else {
        return 'level-none';
      }
    },

    getConsensusLevelLabel(level) {
      if (!level) return 'Sem Análise';
      
      // Usar o primeiro valor do banco se estiver em português, ou traduzir se estiver em inglês
      const levelLower = level.toLowerCase();
      
      if (levelLower.includes('alto') || levelLower.includes('high')) {
        return levelLower.includes('alto') ? level : 'Alto Consenso';
      } else if (levelLower.includes('médio') || levelLower.includes('medium')) {
        return levelLower.includes('médio') ? level : 'Consenso Médio';
      } else if (levelLower.includes('baixo') || levelLower.includes('low')) {
        return levelLower.includes('baixo') ? level : 'Baixo Consenso';
      } else {
        return 'Sem Consenso';
      }
    },
    formatProcessingTime(timeMs) {
      if (!timeMs) return '--';
      
      return timeMs > 1000 
        ? `${(timeMs / 1000).toFixed(2)}s`
        : `${timeMs}ms`;
    },
    formatConsensusScore(score) {
      if (!score) return 0;
      
      // Se o score for maior que 1, está no formato inteiro (por exemplo, 75)
      // Caso contrário, está no formato decimal (por exemplo, 0.75) e precisa ser multiplicado por 100
      return score > 1 ? Math.round(score) : Math.round(score * 100);
    },
    getConsensusDataValue(field) {
      if (!this.selectedPrompt) return null;
      
      // Tentar acessar primeiro em consensusData, depois em consensus_data
      if (this.selectedPrompt.consensusData && this.selectedPrompt.consensusData[field] !== undefined) {
        return this.selectedPrompt.consensusData[field];
      }
      
      if (this.selectedPrompt.consensus_data && this.selectedPrompt.consensus_data[field] !== undefined) {
        return this.selectedPrompt.consensus_data[field];
      }
      
      return null;
    },
    
    hasConsensusPoints(field) {
      const points = this.getConsensusDataValue(field);
      return points && Array.isArray(points) && points.length > 0;
    },
    formatDate(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    /**
     * Retorna o estilo CSS para a persona com base no ID
     * @param {string} personaId - ID da persona
     * @returns {object} - Objeto de estilo CSS
     */
    getPersonaStyle(personaId) {
      if (!personaId || !this.selectedPrompt || !this.selectedPrompt.debate_data) {
        return { backgroundColor: '#6366F1' }; // Cor padrão
      }
      
      const persona = this.findPersonaById(personaId);
      if (persona && persona.color) {
        return { backgroundColor: persona.color };
      }
      
      // Cores específicas para agentes do sistema
      const systemColors = {
        'consensus_reviewer': '#4F46E5',
        'coordinator_initial_context': '#10B981',
        'agent_social_scientist_reasoning': '#F59E0B',
        'agent_social_scientist_evaluation': '#EC4899',
        'consensus_init': '#10B981'
      };
      
      return { backgroundColor: systemColors[personaId] || '#6366F1' };
    },
    
    /**
     * Retorna o avatar da persona com base no ID
     * @param {string} personaId - ID da persona
     * @returns {string|null} - URL do avatar ou null
     */
    getPersonaAvatar(personaId, personasList) {
      if (!personaId || !personasList) return null;
      const persona = this.findPersonaById(personaId, personasList);
      return persona && persona.avatar ? persona.avatar : null;
    },
    
    /**
     * Retorna o nome da persona com base no ID
     * @param {string} personaId - ID da persona
     * @param {string} fallbackName - Nome alternativo se a persona não for encontrada
     * @returns {string} - Nome da persona
     */
    getPersonaName(personaId, fallbackName) {
      if (!personaId || !this.selectedPrompt || !this.selectedPrompt.debate_data) {
        return fallbackName || 'Sistema';
      }
      
      const persona = this.findPersonaById(personaId);
      if (persona && persona.name) {
        return persona.name;
      }
      
      // Nomes específicos para agentes do sistema
      const systemNames = {
        'consensus_reviewer': 'Revisor de Consenso',
        'coordinator_initial_context': 'Coordenador de Debate',
        'agent_social_scientist_reasoning': 'Cientista Social (Planejamento)',
        'agent_social_scientist_evaluation': 'Avaliador de Debate',
        'consensus_init': 'Coordenador de Debate'
      };
      
      return systemNames[personaId] || fallbackName || 'Sistema';
    },
    
    /**
     * Retorna as iniciais do nome da persona
     * @param {string} name - Nome da persona
     * @returns {string} - Iniciais do nome
     */
    getPersonaInitials(name) {
      if (!name) return 'S';
      
      const words = name.split(' ');
      if (words.length === 1) {
        return words[0].charAt(0).toUpperCase();
      }
      
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    },
    
    /**
     * Encontra uma persona pelo ID na lista de personas do debate
     * @param {string} personaId - ID da persona
     * @returns {object|null} - Objeto da persona ou null
     */
    findPersonaById(personaId, personasList) {
      if (!personaId || !personasList) return null;
      return personasList.find(p => p && p.id === personaId) || null;
    },
    
    /**
     * Formata o tempo de resposta em segundos
     * @param {number} timeInSeconds - Tempo em segundos
     * @returns {string} - Tempo formatado
     */
    formatResponseTime(timeInSeconds) {
      if (!timeInSeconds) return '0s';
      return `${Math.round(timeInSeconds * 100) / 100}s`;
    },
    /**
     * Formata uma chave JSON para exibição amigável
     * @param {string} key - Chave JSON
     * @returns {string} - Chave formatada
     */
    formatKey(key) {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
    },
    
    /**
     * Formata o tempo de análise em milissegundos
     * @param {number} timeMs - Tempo em milissegundos
     * @returns {string} - Tempo formatado
     */
    formatAnalysisTime(timeMs) {
      if (!timeMs) return 'N/A';
      
      if (timeMs < 1000) {
        return `${timeMs}ms`;
      }
      
      return `${(timeMs / 1000).toFixed(2)}s`;
    },
    formatPromptInput(promptInput) {
      if (!promptInput) return '';
      
      // Se for um array de objetos (formato comum para prompts)
      if (Array.isArray(promptInput)) {
        return promptInput.map(item => {
          if (typeof item === 'object') {
            return `[${item.role || 'system'}]:\n${item.content || ''}`;
          }
          return item;
        }).join('\n\n');
      }
      
      // Se for um objeto JSON em formato string
      if (typeof promptInput === 'string' && (promptInput.startsWith('[') || promptInput.startsWith('{'))) {
        try {
          const parsed = JSON.parse(promptInput);
          if (Array.isArray(parsed)) {
            return parsed.map(item => {
              if (typeof item === 'object') {
                return `[${item.role || 'system'}]:\n${item.content || ''}`;
              }
              return item;
            }).join('\n\n');
          }
          return JSON.stringify(parsed, null, 2);
        } catch (e) {
          // Se não for JSON válido, retorna o texto original
          return promptInput;
        }
      }
      
      // Caso seja uma string simples
      return promptInput;
    },
    editInsight(item) {
      console.log('Editando insight:', item);
      
      // Extrair personas do item
      let selectedPersonas = [];
      if (item.debate_data && item.debate_data.personas) {
        // Extrair IDs das personas do debate_data
        selectedPersonas = item.debate_data.personas.map(p => String(p.id || p.persona_id));
        console.log('Personas extraídas do debate_data:', selectedPersonas);
      }
      
      // Preparar os dados do insight para edição
      this.editingPrompt = {
        ...item,
        module: item.module || 'reviews',
        module_id: item.module_id !== undefined && item.module_id !== null ? parseInt(item.module_id) : null,
        rounds: item.rounds || 1,
        use_reasoning: item.use_reasoning !== undefined ? item.use_reasoning : true,
        // Usar as personas extraídas do debate_data
        selectedPersonas: selectedPersonas,
        // Carregar as fontes de dados selecionadas, se existirem
        selectedDataSources: item.data_sources || [],
        // Garantir que module_fields tenha uma estrutura válida
        module_fields: item.module_fields || { state: '', city: '' }
      };
      
      console.log('Dados preparados para edição:', this.editingPrompt);
      this.showEditModal = true;
    },
    handleEditSave(config) {
      
      // Verificar se temos o insight original para edição
      if (!this.editingPrompt || !this.editingPrompt.id) {
        this.showToastError({ message: 'Erro ao editar insight: dados originais não encontrados' });
        return;
      }
      
      // Preparar os dados para o versionamento
      const versionData = {
        ...config,
        original_id: this.editingPrompt.id, // ID do insight original para versionamento
        enabled: 1 // Nova versão estará habilitada
      };
      
      // Emitir evento para criar nova versão e desabilitar a anterior
      this.$emit('version-insight', versionData);
      
      // Fechar o modal de edição
      this.closeEditModal();
      
      // Exibir mensagem de sucesso
      this.showToastSuccess('Insight atualizado com sucesso. Nova versão criada.');
      
      // Atualizar a lista de insights
      this.$emit('get-prompts');
    },
  },
  computed: {
    consensusLevelClass() {
      if (!this.selectedPrompt || !this.selectedPrompt.consensus_data) {
        return '';
      }
      
      const level = this.selectedPrompt.consensus_data.consensus_level;
      
      if (!level) return '';
      
      const levelMap = {
        'Alto': 'high-consensus',
        'Médio': 'medium-consensus',
        'Baixo': 'low-consensus'
      };
      
      return levelMap[level] || '';
    },
  },
}
</script>

<style scoped>
/* Estilos para Skeleton Loading */
@keyframes pulse {
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.3;
  }
  100% {
    opacity: 0.6;
  }
}

.skeleton-pulse {
  animation: pulse 1.5s ease-in-out infinite;
  background-color: #e9ecef;
  border-radius: 4px;
}

.skeleton-card {
  position: relative;
  overflow: hidden;
}

.skeleton-avatar {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f0f0f0 !important;
}

.skeleton-avatar .skeleton-pulse {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.skeleton-name {
  height: 24px;
  width: 80%;
  margin-bottom: 8px;
}

.skeleton-role {
  height: 18px;
  width: 60%;
  margin-bottom: 12px;
}

.skeleton-tag {
  height: 22px;
  width: 80px;
  border-radius: 12px;
  display: inline-block;
  margin-right: 8px;
}

.skeleton-action {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  margin: 0 4px;
}

.admin-header {
  display: flex;
  flex-direction: column;
  align-items: self-start;
  width: 550px;
  background-color: #f8fafc;
  padding: 1.5rem;
  border-radius: 0.75rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.admin-header h2 {
  font-size: 1.5rem;
  color: #1e293b;
  margin-bottom: 10px;
}

/* Estilos para campos editáveis */
.edit-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 1.1rem;
  color: #1e293b;
  background-color: white;
  transition: all 0.3s ease;
}

.edit-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.role-select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #64748b;
  background-color: white;
  cursor: pointer;
}

.role-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

/* Estilo do card em modo de edição */
.prompt-card.editing {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

/* Ajuste do container do título */
.title-container {
  flex: 1;
  margin-right: 1rem;
}

/* Estilo para dialog de confirmação */
.confirmation-content {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
}

/* Estilos para as Tabs */
:deep(.p-tabview-nav) {
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 1rem;
}

:deep(.p-tabview-nav-link) {
  padding: 1rem !important;
}

:deep(.p-tabview-selected) {
  border-bottom: 2px solid #3b82f6 !important;
}

/* Estilos para Personas */
.personas-horizontal-scroll {
  overflow-x: auto;
  padding: 16px 0;
  margin-top: 16px;
}

.personas-row {
  display: flex;
  flex-wrap: nowrap;
  gap: 20px;
  padding-bottom: 8px; /* Espaço para scrollbar */
}

.persona-card-enhanced {
  flex: 0 0 300px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border: 2px solid;
  display: flex;
  flex-direction: column;
}

.persona-card-enhanced:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}

.persona-header {
  padding: 16px;
  color: white;
  text-align: center;
  position: relative;
}

.persona-avatar-container {
  margin: 0 auto 12px;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid rgba(255,255,255,0.8);
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.persona-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.persona-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
  border: 2px solid rgba(255, 255, 255, 0.5);
}

.persona-name {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
}

.persona-body {
  padding: 16px;
  background-color: white;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.persona-role {
  margin-bottom: 8px;
  color: #495057;
  font-size: 0.9rem;
}

.persona-description {
  margin: 0 0 16px 0;
  font-size: 0.85rem;
  color: #6c757d;
  line-height: 1.5;
  flex-grow: 1;
}

.persona-model {
  font-size: 0.8rem;
  color: #868e96;
  padding-top: 8px;
  border-top: 1px dashed #e9ecef;
}

.model-label {
  font-weight: 500;
}

.persona-avatar-small {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin-right: 6px;
  object-fit: cover;
  vertical-align: middle;
}

/* Estilos para Debate */
.debate-container {
  padding: 1rem 0;
}

.debate-status {
  margin-bottom: 1.5rem;
}

.debate-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.message {
  background: #f8fafc;
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid #e5e7eb;
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  color: #1e293b;
}

.message-content {
  font-size: 0.925rem;
  color: #334155;
  line-height: 1.5;
}

.message-card {
  background: var(--surface-card);
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--surface-border);
}

.message-header {
  padding: 0.75rem;
  background-color: var(--surface-section);
  border-bottom: 1px solid var(--surface-border);
}

.persona-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  color: white;
  background-color: #4c6ef5;
}

.persona-badge i {
  font-size: 1rem;
}

.persona-badge.coordinator {
  background: #E3F2FD;
  color: #1565C0;
}

.persona-badge.social_scientist {
  background: #F3E5F5;
  color: #7B1FA2;
}

.persona-badge.economist {
  background: #E8F5E9;
  color: #2E7D32;
}

.message-time {
  font-size: 0.8rem;
  color: #868e96;
}

.message-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.prompt strong, .response strong {
  display: block;
  margin-bottom: 0.25rem;
  color: #495057;
}

.prompt p, .response p {
  margin: 0.25rem 0;
  font-size: 0.9rem;
  color: #495057;
  line-height: 1.5;
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 4px;
}

.debate-metrics {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.metric-card {
  flex: 1;
  background: var(--surface-card);
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--surface-border);
}

.metric-card i {
  font-size: 1.5rem;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
}

.metric-value {
  display: block;
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--text-color);
}

.metric-label {
  font-size: 0.85rem;
  color: var(--text-color-secondary);
}

.debate-timeline {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.full-text-content {
  max-height: 70vh;
  overflow-y: auto;
  padding: 1rem;
}

/* Estilos para Personas */
.personas-list-view {
  padding: 1rem 0;
}

.personas-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.personas-header h2 {
  font-size: 1.5rem;
  color: var(--text-color);
  margin: 0;
}

.personas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.persona-list-card {
  background: var(--surface-card);
  border-radius: 10px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--surface-border);
}

.persona-list-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.persona-list-avatar {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  flex-shrink: 0;
  font-size: 1.5rem;
  color: white;
  overflow: hidden;
}

.persona-list-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.persona-list-info {
  flex: 1;
}

.persona-list-info h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-color);
}

.persona-role {
  margin-bottom: 8px;
  color: #495057;
  font-size: 0.9rem;
}

.persona-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.expertise-tag {
  background-color: #e1f5fe;
  color: #1976d2;
  border-radius: 12px;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
}

.trait-tag {
  background-color: #f3e5f5;
  color: #7b1fa2;
  border-radius: 12px;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
}

.persona-list-actions {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.5rem;
}

/* Formulário de edição de persona */
.persona-form {
  padding: 1rem 0;
}

.persona-form h2 {
  margin: 0 0 1.5rem 0;
  font-size: 1.5rem;
  color: var(--text-color);
}

.form-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.avatar-section {
  flex: 0 0 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.avatar-preview {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  font-size: 2rem;
  color: white;
}

.avatar-preview:hover {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.avatar-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.form-fields {
  flex: 1;
}

.nested-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.field {
  flex: 0 0 calc(50% - 0.5rem);
  margin-bottom: 1rem;
}

.field.full-width {
  flex: 0 0 100%;
}

.field label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-color);
}

.helper-text {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--surface-border);
}

.add-new-item {
  display: flex;
  margin-top: 0.5rem;
  gap: 0.5rem;
}

.multiselect-field {
  width: 100%;
}

.multiselect-token {
  display: inline-flex;
  align-items: center;
  background: #e3f2fd;
  color: #1976d2;
  border-radius: 1rem;
  padding: 0.2rem 0.6rem;
  font-size: 0.85rem;
  display: inline-flex;
  align-items: center;
}

.expertise-token-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.token-remove-btn {
  width: 1.2rem !important;
  height: 1.2rem !important;
  padding: 0 !important;
  margin-left: 0.3rem;
}

.token-remove-btn :deep(.p-button-icon) {
  font-size: 0.7rem;
}

.multiselect-token:hover {
  background: #bbdefb;
}

.empty-personas-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  padding: 3rem 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.empty-personas-content {
  text-align: center;
  max-width: 500px;
}

.empty-personas-text {
  margin: 1.5rem 0;
}

.empty-personas-text h3 {
  font-size: 1.5rem;
  color: #495057;
  margin-bottom: 0.5rem;
}

.empty-personas-text p {
  color: #6c757d;
  font-size: 1rem;
  line-height: 1.5;
}

.fas.fa-users {
  color: #6366f1;
  margin-bottom: 1rem;
}

/* Responsivo */
@media (max-width: 768px) {
  .personas-grid {
    grid-template-columns: 1fr;
  }
  
  .form-grid {
    flex-direction: column;
  }
  
  .avatar-section {
    width: 100%;
    margin-bottom: 1.5rem;
  }
  
  .field {
    flex: 0 0 100%;
  }
}

/* Estilo para os cards de prompt simplificados */
.prompt-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.prompt-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  border: 1px solid #e9ecef;
}

.prompt-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.prompt-card-header {
  padding: 1rem;
  border-bottom: 1px solid #f1f1f1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
}

.prompt-card-content {
  padding: 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.prompt-info {
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  color: #6c757d;
  font-size: 0.85rem;
}

.prompt-text {
  margin: 0.75rem 0;
  color: #495057;
  font-size: 0.9rem;
  line-height: 1.5;
  flex: 1;
}

.prompt-role {
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px dashed #e9ecef;
  color: #6c757d;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
}

.prompt-card-actions {
  padding: 0.75rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  border-top: 1px solid #f1f1f1;
  background-color: #f8f9fa;
}

/* Estilos para o modal de detalhes do prompt */
.prompt-details-container {
  padding: 0.5rem;
}

.personas-container {
  margin: 1rem 0;
}

.personas-horizontal-scroll {
  overflow-x: auto;
  padding-bottom: 1rem;
}

.personas-row {
  display: flex;
  gap: 1.5rem;
  padding: 0.5rem;
}

.persona-card-enhanced {
  min-width: 280px;
  max-width: 320px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  border: 2px solid #e9ecef;
  overflow: hidden;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
}

.persona-card-enhanced:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.persona-header {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
}

.persona-avatar-container {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  margin-bottom: 0.75rem;
  overflow: hidden;
}

.persona-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.persona-avatar-container i {
  font-size: 2rem;
  color: white;
}

.persona-name {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
}

.persona-body {
  padding: 1rem;
}

.persona-role {
  margin-bottom: 0.75rem;
  color: #495057;
  font-size: 0.9rem;
}

.persona-description {
  margin: 0 0 0.75rem 0;
  font-size: 0.85rem;
  color: #6c757d;
  line-height: 1.5;
  flex-grow: 1;
}

.persona-model {
  font-size: 0.8rem;
  color: #868e96;
  padding-top: 0.5rem;
  border-top: 1px dashed #e9ecef;
}

.role-label {
  font-weight: 500;
}

.persona-avatar-small {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin-right: 6px;
  object-fit: cover;
  vertical-align: middle;
}

/* Estilos para a aba de debate */
.debate-container {
  margin: 1rem 0;
}

.debate-metrics {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.metric-card {
  background: var(--surface-card);
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--surface-border);
}

.metric-value {
  display: block;
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--text-color);
}

.metric-label {
  font-size: 0.85rem;
  color: var(--text-color-secondary);
}

.debate-timeline {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.message-card {
  background: var(--surface-card);
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--surface-border);
}

.message-header {
  padding: 0.75rem;
  background-color: var(--surface-section);
  border-bottom: 1px solid var(--surface-border);
}

.persona-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: 16px;
  font-size: 0.85rem;
  color: white;
  background-color: #4c6ef5;
}

.persona-avatar-small {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  vertical-align: middle;
}

.message-time {
  font-size: 0.8rem;
  color: #868e96;
}

.message-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.prompt strong, .response strong {
  display: block;
  margin-bottom: 0.25rem;
  color: #495057;
}

.prompt p, .response p {
  margin: 0.25rem 0;
  font-size: 0.9rem;
  color: #495057;
  line-height: 1.5;
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 4px;
}

.no-personas, .no-debate {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #6c757d;
  background: #f8f9fa;
  border-radius: 8px;
  text-align: center;
}

.no-personas i, .no-debate i {
  font-size: 2.5rem;
  color: #ced4da;
  margin-bottom: 1rem;
}

.title-container {
  flex: 1;
}

.title-container h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
}

.edit-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-processing {
  background-color: #e3f2fd;
  color: #0277bd;
}

.status-complete {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.status-error {
  background-color: #ffebee;
  color: #c62828;
}

/* Responsivo */
@media (max-width: 768px) {
  .prompt-cards-grid {
    grid-template-columns: 1fr;
  }
  
  .personas-row {
    flex-direction: column;
  }
  
  .persona-card-enhanced {
    min-width: 100%;
    max-width: none;
  }
  
  .debate-metrics {
    flex-direction: column;
    gap: 1rem;
  }
}

/* Estilo para a aba Consensus */
.consensus-container {
  padding: 1rem 0;
}

.consensus-header-card {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.consensus-score-visual {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.score-ring {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border: 5px solid;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.score-value {
  font-size: 1.8rem;
  font-weight: bold;
}

.level-high {
  border-color: #4caf50;
  color: #2e7d32;
  background-color: #e8f5e9;
}

.level-medium {
  border-color: #ff9800;
  color: #e65100;
  background-color: #fff3e0;
}

.level-low {
  border-color: #f44336;
  color: #c62828;
  background-color: #ffebee;
}

.level-none {
  border-color: #9e9e9e;
  color: #424242;
  background-color: #f5f5f5;
}

.score-label {
  display: flex;
  flex-direction: column;
}

.score-label span {
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.score-label h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #212529;
}

.consensus-meta {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.meta-item {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
}

.consensus-section {
  margin-bottom: 1.5rem;
}

.consensus-section h3 {
  font-size: 1.1rem;
  margin-bottom: 0.75rem;
  color: #374151;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 0.5rem;
}

.consensus-section ul {
  padding-left: 1.5rem;
  margin: 0;
}

.consensus-section li {
  margin-bottom: 0.5rem;
  line-height: 1.5;
}

.function-response {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
}

.function-response-item {
  margin-bottom: 0.75rem;
}

.function-response-item strong {
  display: block;
  margin-bottom: 0.25rem;
  color: #4b5563;
}

.consensus-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #eaeaea;
  font-size: 0.85rem;
  color: #6b7280;
}

.meta-item {
  display: flex;
  align-items: center;
}

.meta-label {
  font-weight: 600;
  margin-right: 0.25rem;
}

.no-data {
  color: #9ca3af;
  font-style: italic;
}

.no-consensus-data {
  padding: 2rem;
  text-align: center;
  color: #6b7280;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* Classes para nu00edveis de consenso */
.high-consensus {
  color: #047857;
  border-color: #047857;
}

.medium-consensus {
  color: #d97706;
  border-color: #d97706;
}

.low-consensus {
  color: #dc2626;
  border-color: #dc2626;
}

/* Estilos para as tabs na timeline */
.timeline-tabs {
  margin-bottom: 1rem;
  width: 100%;
}

.timeline-tabs .p-tabview-nav {
  border-bottom: 1px solid var(--surface-border);
}

.timeline-tabs .p-tabview-nav li .p-tabview-nav-link {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
}

.timeline-tabs .p-tabview-panels {
  padding: 1rem 0;
}

.timeline-item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  background-color: var(--surface-hover);
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin-top: 0.5rem;
}

.timeline-item-meta-item {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
}

.timeline-item-meta-label {
  font-weight: 600;
  margin-right: 0.25rem;
  color: var(--text-color-secondary);
}

.timeline-item-meta-value {
  color: var(--text-color);
}

/* Ajustes para o layout da timeline */
.message-card {
  margin-bottom: 1.5rem;
  border: 1px solid var(--surface-border);
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  background-color: white;
}

.message-header {
  padding: 0.75rem;
  background-color: var(--surface-section);
  border-bottom: 1px solid var(--surface-border);
}

.consensus-meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.consensus-synthesis-card {
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.consensus-synthesis {
  padding: 1rem;
}

.consensus-points-container {
  display: flex;
  gap: 1.5rem;
  padding: 1rem;
}

.consensus-points-card {
  background: var(--surface-card);
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--surface-border);
}

.points-list {
  padding: 1rem;
}

.point-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.point-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 0.85rem;
  font-weight: 600;
  flex-shrink: 0;
}

.point-content {
  flex: 1;
  font-size: 0.9rem;
  color: #495057;
}

.point-consensus {
  border-left: 4px solid #4caf50;
}

.point-divergence {
  border-left: 4px solid #f44336;
}

.no-consensus {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.no-consensus-content {
  text-align: center;
  max-width: 500px;
  padding: 2rem;
}

.no-consensus-content i {
  font-size: 3rem;
  color: #adb5bd;
  margin-bottom: 1rem;
}

.no-consensus-content h3 {
  margin-bottom: 1rem;
  font-size: 1.2rem;
  color: #495057;
}

.no-consensus-content p {
  color: #6c757d;
  font-size: 0.95rem;
  line-height: 1.5;
}

/* Responsividade para Consensus */
@media (max-width: 768px) {
  .consensus-header-card {
    flex-direction: column;
    gap: 1.5rem;
    align-items: flex-start;
  }
  
  .consensus-score-visual {
    width: 100%;
    justify-content: space-between;
  }
}

/* Estilos para a aba de consenso */
.consensus-container {
  padding: 1rem 0;
}

.consensus-header-card {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.consensus-score-visual {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.score-ring {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border: 5px solid;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.score-value {
  font-size: 1.8rem;
  font-weight: bold;
}

.level-high {
  border-color: #4caf50;
  color: #2e7d32;
  background-color: #e8f5e9;
}

.level-medium {
  border-color: #ff9800;
  color: #e65100;
  background-color: #fff3e0;
}

.level-low {
  border-color: #f44336;
  color: #c62828;
  background-color: #ffebee;
}

.level-none {
  border-color: #9e9e9e;
  color: #424242;
  background-color: #f5f5f5;
}

.score-label {
  display: flex;
  flex-direction: column;
}

.score-label span {
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.score-label h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #212529;
}

.consensus-meta {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.meta-item {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
}

.consensus-section {
  margin-bottom: 1.5rem;
}

.consensus-section h3 {
  font-size: 1.1rem;
  margin-bottom: 0.75rem;
  color: #374151;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 0.5rem;
}

.consensus-section ul {
  padding-left: 1.5rem;
  margin: 0;
}

.consensus-section li {
  margin-bottom: 0.5rem;
  line-height: 1.5;
}

.function-response {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
}

.function-response-item {
  margin-bottom: 0.75rem;
}

.function-response-item strong {
  display: block;
  margin-bottom: 0.25rem;
  color: #4b5563;
}

.consensus-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #eaeaea;
  font-size: 0.85rem;
  color: #6b7280;
}

.meta-item {
  display: flex;
  align-items: center;
}

.meta-label {
  font-weight: 600;
  margin-right: 0.25rem;
}

.no-data {
  color: #9ca3af;
  font-style: italic;
}

.no-consensus-data {
  padding: 2rem;
  text-align: center;
  color: #6b7280;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* Classes para nu00edveis de consenso */
.high-consensus {
  color: #047857;
  border-color: #047857;
}

.medium-consensus {
  color: #d97706;
  border-color: #d97706;
}

.low-consensus {
  color: #dc2626;
  border-color: #dc2626;
}

/* Estilos para as tabs na timeline */
.timeline-tabs {
  margin-bottom: 1rem;
  width: 100%;
}

.timeline-tabs .p-tabview-nav {
  border-bottom: 1px solid var(--surface-border);
}

.timeline-tabs .p-tabview-nav li .p-tabview-nav-link {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
}

.timeline-tabs .p-tabview-panels {
  padding: 1rem 0;
}

.timeline-item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  background-color: var(--surface-hover);
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin-top: 0.5rem;
}

.timeline-item-meta-item {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
}

.timeline-item-meta-label {
  font-weight: 600;
  margin-right: 0.25rem;
  color: var(--text-color-secondary);
}

.timeline-item-meta-value {
  color: var(--text-color);
}

/* Ajustes para o layout da timeline */
.message-card {
  margin-bottom: 1.5rem;
  border: 1px solid var(--surface-border);
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  background-color: white;
}

.message-header {
  padding: 0.75rem;
  background-color: var(--surface-section);
  border-bottom: 1px solid var(--surface-border);
}

.timeline-item-content-text {
  white-space: pre-wrap;       /* Preserva quebras de linha e quebra o texto */
  word-wrap: break-word;       /* Quebra palavras longas */
  overflow-wrap: break-word;   /* Quebra palavras longas (padrão mais novo) */
  max-width: 100%;             /* Limita a largura ao container pai */
  font-family: inherit;        /* Usa a mesma fonte do resto do site */
  margin: 0;                   /* Remove margens padrão */
  padding: 1rem;               /* Adiciona padding interno */
  background-color: #f8f9fa;   /* Fundo claro para melhor legibilidade */
  border-radius: 4px;          /* Cantos arredondados */
  font-size: 0.95rem;          /* Tamanho de fonte ligeiramente menor */
  line-height: 1.5;            /* Espaçamento entre linhas para melhor legibilidade */
}
</style>
